import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

export const Gallery = () => {
  const { t } = useTranslation(['home', 'common']);
  const swiperRef = useRef<SwiperType>();

  // Lista di tutte le immagini nella cartella img
  const images = [
    '/img/DSC03172.JPG',
    '/img/DSC03238.JPG',
    '/img/DSC03287.JPG',
    '/img/DSC09135 (2024-04-22T17_53_48.520).JPG',
    '/img/DSC09190 (2024-04-22T17_54_40.682).JPG',
    '/img/DSC09226 (2024-04-22T17_54_53.375).JPG',
    '/img/DSC09236 (2024-04-22T17_55_00.114).JPG',
    '/img/DSC09243 (2024-04-22T17_55_05.360).JPG',
    '/img/DSC09272 (2024-04-22T17_55_41.962).JPG',
    '/img/DSC09291.JPG',
    '/img/DSC09323 (2024-04-22T17_56_33.034).JPG',
  ];

  return (
    <section id="galleria" className="bg-card py-16 md:py-32 px-4 md:px-6" aria-labelledby="galleria-heading">
      <div className="mx-auto max-w-7xl">
        <ScrollReveal>
          <div className="mb-16 text-center">
            <span className="mb-4 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
              {t('home:gallery.label')}
            </span>
            <h2 id="galleria-heading" className="text-4xl font-semibold text-foreground md:text-5xl">
              {t('home:gallery.title')}
            </h2>
            <p className="mt-4 text-muted-foreground">{t('home:gallery.subtitle')}</p>
          </div>
        </ScrollReveal>

        {/* Swiper Slider */}
        <ScrollReveal delay={100}>
          <div className="relative w-full max-w-full overflow-hidden">
            <Swiper
              modules={[Navigation, Pagination, Autoplay, EffectFade]}
              spaceBetween={30}
              slidesPerView={1}
              navigation={{
                prevEl: '.swiper-button-prev-custom',
                nextEl: '.swiper-button-next-custom',
                enabled: false,
              }}
              breakpoints={{
                768: {
                  navigation: {
                    enabled: true,
                  },
                },
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              effect="fade"
              fadeEffect={{
                crossFade: true,
              }}
              loop={true}
              onBeforeInit={(swiper) => {
                swiperRef.current = swiper;
              }}
              className="!pb-12 !w-full !max-w-full"
              style={{ width: '100%', maxWidth: '100%' }}
            >
              {images.map((image, index) => (
                <SwiperSlide key={index} className="!w-full">
                  <div className="relative aspect-[4/3] overflow-hidden rounded-lg w-full">
                    <img
                      src={image}
                      alt={t('home:gallery.slideAlt', { n: index + 1 })}
                      className="h-full w-full object-cover"
                      loading={index < 1 ? 'eager' : 'lazy'}
                      decoding="async"
                      width="1200"
                      height="900"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Custom Navigation Buttons - Hidden on mobile */}
            <button
              type="button"
              className="focus-ring swiper-button-prev-custom hidden md:flex absolute left-4 top-1/2 z-10 -translate-y-1/2 h-12 w-12 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm text-foreground transition-all hover:bg-background hover:text-primary"
              aria-label={t('common:aria.prevSlide')}
            >
              <svg
                className="h-6 w-6"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              type="button"
              className="focus-ring swiper-button-next-custom hidden md:flex absolute right-4 top-1/2 z-10 -translate-y-1/2 h-12 w-12 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm text-foreground transition-all hover:bg-background hover:text-primary"
              aria-label={t('common:aria.nextSlide')}
            >
              <svg
                className="h-6 w-6"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};
